import { ChangeEvent, FormEvent, useState } from 'react';
import { motion } from 'framer-motion';
import { NEONVERSE } from '../constants';

const LENGTH_OPTIONS = [
  'Up to 3 minutes (starting package)',
  '4–5 minutes',
  '6+ minutes',
  'Not sure yet',
];

const INITIAL = {
  name: '',
  email: '',
  phone: '',
  occasion: '',
  genre: '',
  length: '',
  language: '',
  mood: '',
  story: '',
  specialRequests: '',
};

type Status = 'idle' | 'loading' | 'success' | 'error';

const NeonContact = () => {
  const [form, setForm] = useState(INITIAL);
  const [status, setStatus] = useState<Status>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const set =
    (key: keyof typeof INITIAL) =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      setForm((prev) => ({ ...prev, [key]: e.target.value }));
    };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const accessKey = process.env.REACT_APP_NEONVERSE_ACCESS_KEY;
    if (!accessKey) {
      setStatus('error');
      setErrorMsg('Form is not configured yet. Please try again later.');
      return;
    }

    setStatus('loading');
    setErrorMsg('');

    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          access_key: accessKey,
          subject: `Neon Verse Song Request — ${form.name}`,
          from_name: form.name,
          name: form.name,
          email: form.email,
          phone: form.phone,
          occasion: form.occasion,
          genre: form.genre,
          desired_length: form.length,
          language: form.language || 'Not specified',
          mood: form.mood || 'Not specified',
          story: form.story,
          special_requests: form.specialRequests || 'None',
        }),
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Submission failed');
      }

      setForm(INITIAL);
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrorMsg(
        err instanceof Error ? err.message : 'Something went wrong. Please try again.'
      );
    }
  };

  return (
    <section className="nv-section nv-contact" id="contact">
      <div className="nv-container">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.55 }}
        >
          <p className="nv-eyebrow">Start your song</p>
          <h2 className="nv-title">Have an idea?</h2>
          <p className="nv-lead">
            Share your story and preferences — we&apos;ll review your request and reply by email.
          </p>

          {status === 'success' ? (
            <div className="nv-form__success" role="status">
              <p className="nv-form__success-title">Request received</p>
              <p>
                We&apos;ll review your story and reply by email with next steps. Thank you for
                sharing it with Neon Verse Studio.
              </p>
              <button
                type="button"
                className="nv-btn nv-btn--ghost"
                style={{ marginTop: '1.25rem' }}
                onClick={() => setStatus('idle')}
              >
                Submit another request
              </button>
            </div>
          ) : (
            <form className="nv-form" onSubmit={onSubmit}>
              <div className="nv-form__grid">
                <label className="nv-field">
                  <span>Name *</span>
                  <input
                    name="name"
                    type="text"
                    required
                    autoComplete="name"
                    value={form.name}
                    onChange={set('name')}
                    placeholder="Your name"
                  />
                </label>

                <label className="nv-field">
                  <span>Email *</span>
                  <input
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    value={form.email}
                    onChange={set('email')}
                    placeholder="you@example.com"
                  />
                </label>

                <label className="nv-field">
                  <span>Phone *</span>
                  <input
                    name="phone"
                    type="tel"
                    required
                    autoComplete="tel"
                    value={form.phone}
                    onChange={set('phone')}
                    placeholder="Your phone number"
                  />
                </label>

                <label className="nv-field">
                  <span>Occasion *</span>
                  <select
                    name="occasion"
                    required
                    value={form.occasion}
                    onChange={set('occasion')}
                  >
                    <option value="">Select an occasion</option>
                    {NEONVERSE.services.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                    <option value="Other">Other</option>
                  </select>
                </label>

                <label className="nv-field">
                  <span>Genre / style *</span>
                  <select name="genre" required value={form.genre} onChange={set('genre')}>
                    <option value="">Select a genre</option>
                    {NEONVERSE.styles.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                    <option value="Other">Other</option>
                  </select>
                </label>

                <label className="nv-field">
                  <span>Desired length *</span>
                  <select name="length" required value={form.length} onChange={set('length')}>
                    <option value="">Select length</option>
                    {LENGTH_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="nv-field">
                  <span>Language</span>
                  <input
                    name="language"
                    type="text"
                    value={form.language}
                    onChange={set('language')}
                    placeholder="e.g. English, Hindi"
                  />
                </label>

                <label className="nv-field">
                  <span>Mood / tone</span>
                  <input
                    name="mood"
                    type="text"
                    value={form.mood}
                    onChange={set('mood')}
                    placeholder="e.g. joyful, cinematic, romantic"
                  />
                </label>
              </div>

              <label className="nv-field nv-field--full">
                <span>Your story *</span>
                <textarea
                  name="story"
                  required
                  rows={5}
                  value={form.story}
                  onChange={set('story')}
                  placeholder="Share memories, names, dates, emotions, and the vision for your song…"
                />
              </label>

              <label className="nv-field nv-field--full">
                <span>Special requests</span>
                <textarea
                  name="specialRequests"
                  rows={3}
                  value={form.specialRequests}
                  onChange={set('specialRequests')}
                  placeholder="Inside jokes, must-include lines, vocal preference, instruments…"
                />
              </label>

              {status === 'error' && (
                <p className="nv-form__error" role="alert">
                  {errorMsg}
                </p>
              )}

              <button
                type="submit"
                className="nv-btn nv-btn--primary"
                disabled={status === 'loading'}
              >
                {status === 'loading' ? 'Sending…' : 'Submit your story'}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default NeonContact;
